import { EntryCards, ProductGrid } from '../components/ProductGrid';
import { AgentPrompt } from '../components/AgentPrompt';

/**
 * The home page — ported from the approved mock
 * (work/docs-design/src/docs.html `data-page="home"`): one sentence, three entry cards, the
 * product row and the "Give this to your agent" block. A custom-page rather than an MDX doc so
 * the entry-card grid and the agent-prompt block can be React components sharing the same
 * canon-token styling as every other Swaps surface, not a Zudoku landing-page template.
 */
export default function Home() {
  return (
    <div style={{ maxWidth: 1080, margin: '0 auto', padding: '40px 24px 96px' }}>
      <h1
        style={{
          fontSize: 36,
          fontWeight: 700,
          letterSpacing: '-0.025em',
          lineHeight: 1.18,
          maxWidth: '16ch',
          margin: '8px 0 28px',
        }}
      >
        Move money over an API your agent can use.
      </h1>

      <EntryCards
        cards={[
          {
            title: 'Get started',
            description:
              'Get a business key, read your capabilities and draft your first payment link.',
            href: '/get-started',
            cta: 'Start building',
          },
          {
            title: 'API reference',
            description:
              'Every resource, grouped like the OpenAPI tags, with request and response samples.',
            href: '/reference',
            cta: 'Browse the reference',
          },
          {
            title: 'Agents & MCP',
            description:
              'Connect the hosted MCP server and call tools instead of writing HTTP calls.',
            href: '/agents-mcp',
            cta: 'Connect an agent',
          },
        ]}
      />

      <h2
        style={{
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: '0.02em',
          color: 'var(--muted-foreground)',
          textTransform: 'uppercase',
          margin: '0 0 14px',
        }}
      >
        Products
      </h2>
      <ProductGrid
        tiles={[
          {
            title: 'Payment links',
            status: 'available',
            description: 'Request money with a hosted link — bank, crypto, or both.',
            href: '/products/payment-links',
          },
          {
            title: 'Pay an invoice',
            status: 'available',
            description: 'Send stablecoins that settle as a bank payout for a supplier.',
            href: '/products/pay-invoice',
          },
          {
            title: 'Payroll',
            status: 'available',
            description: 'Draft a pay run; recipients add their own destination afterwards.',
            href: '/products/payroll',
          },
          {
            title: 'Wallet',
            status: 'dark-flag',
            description: 'A non-custodial Tempo balance — deposits, sends and conversions.',
            href: '/products/wallet',
          },
          {
            title: 'Buy & sell',
            status: 'available',
            description:
              'Quote and route a crypto purchase or sale across every connected provider.',
            href: '/products/buy-sell',
          },
          {
            title: 'Crypto processing',
            status: 'dark-flag',
            description: 'Subscriptions and a crypto-only rail, paid one tap at a time.',
            href: '/products/crypto-processing',
          },
        ]}
      />

      <h2
        style={{
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: '0.02em',
          color: 'var(--muted-foreground)',
          textTransform: 'uppercase',
          margin: '0 0 14px',
        }}
      >
        Give this to your agent
      </h2>
      <AgentPrompt
        title="Point an agent at Swaps and it can read this page for you."
        description="Paste this into Claude, ChatGPT or your own agent framework once it is connected to the hosted MCP server with a live Swaps business key — payment links are unavailable in test mode."
        prompt={
          'Connect to the Swaps MCP server at mcp.agent.swaps.app. Call list_capabilities first, then draft a $5 payment link for Supa Labs titled "Design retainer — September". Never set attestation_accepted — activating a link is a human step, not yours.'
        }
      />
    </div>
  );
}
